import {
  addDays,
  addHours,
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  endOfDay,
  format,
  getDay,
  isBefore,
  isEqual,
  isSameDay,
  isWithinInterval,
  setHours,
  setMinutes,
  startOfDay,
  subDays,
} from 'date-fns'
import {View} from '../components/nav/Navigation'
import {
  DayHours,
  DefaultRecourse,
  FieldProps,
  ProcessedEvent,
  ResourceFields,
  SchedulerProps,
  ConvertedHoursOfOperation,
  DailyAllowableHours,
  DailyHeadcountBounds,
  HeadcountSlotBounds,
  HourAllowableHours,
  HourHeadcountBounds,
  Position,
  SlotAllowableHours,
} from '../types'
import {StateEvent} from '../components/views/Editor'
import {BaseEmployeeModel, LaborShiftModel} from '../core/_models'
import {boolean} from 'yup'
import {WeekProps} from '../components/views/Week'

export const getOneView = (state: Partial<SchedulerProps>): View => {
  // if (state.month) {
  //   return 'month'
  // } else

  if (state.week) {
    return 'week'
  } else if (state.day) {
    return 'timeline'
  }
  throw new Error('No views were selected')
}

export const getAvailableViews = (state: SchedulerProps) => {
  const views: View[] = []
  // if (state.month) {
  //   views.push('month')
  // }
  if (state.week) {
    views.push('week')
  }
  // if (state.day) {
  //   views.push('day')
  // }

  views.push('timeline')

  return views
}

export const arraytizeFieldVal = (field: FieldProps, val: any, event?: StateEvent) => {
  const arrytize = field.config?.multiple && !Array.isArray(event?.[field.name] || field.default)
  const value = arrytize ? (val ? [val] : []) : val
  const validity = arrytize ? value.length : value
  return {value, validity}
}

export const getResourcedEvents = (
  events: ProcessedEvent[],
  resource: DefaultRecourse,
  resourceFields: ResourceFields,
  fields: FieldProps[]
): ProcessedEvent[] => {
  const keyName = resourceFields.idField
  const resourceField = fields.find((f) => f.name === keyName)
  const isMultiple = !!resourceField?.config?.multiple
  const recousedEvents = []

  for (const event of events) {
    // Handle single select & multiple select accordingly
    const arrytize = isMultiple && !Array.isArray(event[keyName])
    const eventVal = arrytize ? [event[keyName]] : event[keyName]
    const isThisResource = isMultiple
      ? eventVal.includes(resource[keyName])
      : eventVal === resource[keyName]

    if (isThisResource) {
      recousedEvents.push({
        ...event,
        color: event.color || resource[resourceFields.colorField || ''],
      })
    }
  }

  return recousedEvents
}

export const traversCrossingEvents = (
  todayEvents: ProcessedEvent[],
  event: ProcessedEvent
): ProcessedEvent[] => {
  return todayEvents.filter(
    (e) =>
      e.event_id !== event.event_id &&
      (isWithinInterval(addMinutes(event.start, 1), {
        start: e.start,
        end: e.end,
      }) ||
        isWithinInterval(addMinutes(event.end, -1), {
          start: e.start,
          end: e.end,
        }) ||
        isWithinInterval(addMinutes(e.start, 1), {
          start: event.start,
          end: event.end,
        }) ||
        isWithinInterval(addMinutes(e.end, -1), {
          start: event.start,
          end: event.end,
        }))
  )
}

export const filterEventsBetween = (
  events: ProcessedEvent[],
  start: Date,
  end: Date,
  timeZone?: string,
  removeUncountable?: boolean,
  availablePositions?: Position[]
) => {
  const list: ProcessedEvent[] = []
  const positionDict: {[position: string]: boolean} = {}
  if (availablePositions) {
    availablePositions.forEach((item) => {
      positionDict[item.position] = item.is_countable
    })
  }
  for (let i = 0; i < events.length; i++) {
    if (removeUncountable && positionDict[events[i].position] === false) {
      continue
    }
    const event = convertEventTimeZone(events[i], timeZone)
    // check if there is an overlap
    // add 1 minute to start and end to avoid the case where the event is exactly at the start or end of the range
    if (
      isWithinInterval(addMinutes(event.start, 1), {
        start,
        end,
      }) ||
      isWithinInterval(addMinutes(event.end, -1), {
        start,
        end,
      }) ||
      isWithinInterval(addMinutes(start, 1), {
        start: event.start,
        end: event.end,
      }) ||
      isWithinInterval(addMinutes(end, -1), {
        start: event.start,
        end: event.end,
      })
    ) {
      list.push(event)
    }
  }

  return list
}

export const filterEventsBetweenByPosition = (
  events: ProcessedEvent[],
  start: Date,
  end: Date,
  timeZone?: string,
  position?: string
) => {
  const list: ProcessedEvent[] = []
  for (let i = 0; i < events.length; i++) {
    const event = convertEventTimeZone(events[i], timeZone)
    // check if there is an overlap
    // add 1 minute to start and end to avoid the case where the event is exactly at the start or end of the range
    if (
      isWithinInterval(addMinutes(event.start, 1), {
        start,
        end,
      }) ||
      isWithinInterval(addMinutes(event.end, -1), {
        start,
        end,
      }) ||
      isWithinInterval(addMinutes(start, 1), {
        start: event.start,
        end: event.end,
      }) ||
      isWithinInterval(addMinutes(end, -1), {
        start: event.start,
        end: event.end,
      })
    ) {
      list.push(event)
    }
  }

  return list
}

export const parseDateWithoutTimezone = (date: string) => {
  // parses the date with format yyyy-MM-dd
  const [year, month, day] = date.split('-')
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
}

export const calcMinuteHeight = (cellHeight: number, step: number) => {
  return cellHeight / step
  return Math.ceil(cellHeight) / step
}

export const calcCellHeight = (tableHeight: number, hoursLength: number) => {
  return Math.max(tableHeight / hoursLength, 60)
}

export const differenceInDaysOmitTime = (start: Date, end: Date) => {
  return differenceInDays(endOfDay(end), startOfDay(start))
}

export const filterTodayEvents = (events: ProcessedEvent[], today: Date, timeZone?: string) => {
  const list: ProcessedEvent[] = []
  for (let i = 0; i < events.length; i++) {
    const event = convertEventTimeZone(events[i], timeZone)
    if (!event.allDay) {
      list.push(event)
    }
  }
  return list.sort((a, b) => a.end.getTime() - b.end.getTime())
}

export const filterTodayEventsForResource = (
  events: ProcessedEvent[],
  today: Date,
  resource: DefaultRecourse,
  resourceFields: ResourceFields,
  fields: FieldProps[],
  timeZone?: string
) => {
  const list: ProcessedEvent[] = []
  for (let i = 0; i < events.length; i++) {
    const event = convertEventTimeZone(events[i], timeZone)
    const eventBusinessDay =
      typeof event.businessDay === 'string'
        ? parseDateWithoutTimezone(event.businessDay)
        : event.businessDay
    if (
      !event.allDay &&
      getResourcedEvents([event], resource, resourceFields, fields).length &&
      eventBusinessDay.getDate() === today.getDate()
    ) {
      list.push(event)
    }
  }
  return list.sort((a, b) => a.end.getTime() - b.end.getTime())
}

export const filterMultiDaySlot = (
  events: ProcessedEvent[],
  date: Date | Date[],
  timeZone?: string
) => {
  const list: ProcessedEvent[] = []
  for (let i = 0; i < events.length; i++) {
    const event = convertEventTimeZone(events[i], timeZone)
    let withinSlot = event.allDay || differenceInDaysOmitTime(event.start, event.end) > 0
    if (!withinSlot) continue
    if (Array.isArray(date)) {
      withinSlot = date.some((weekday) =>
        isWithinInterval(weekday, {
          start: startOfDay(event.start),
          end: endOfDay(event.end),
        })
      )
    } else {
      withinSlot = isWithinInterval(date, {
        start: startOfDay(event.start),
        end: endOfDay(event.end),
      })
    }

    if (withinSlot) {
      list.push(event)
    }
  }

  return list
}

export const convertEventTimeZone = (event: ProcessedEvent, timeZone?: string) => {
  // Without a timeZone the Intl round-trip below is an identity transform
  // (event dates never carry sub-second precision), and it runs on every
  // event × slot in the hot filters — return the event untouched. Callers
  // then hold the SAME object the store holds: never mutate it in place.
  if (!timeZone) return event
  return {
    ...event,
    start: getTimeZonedDate(event.start, timeZone),
    end: getTimeZonedDate(event.end, timeZone),
  }
}

export const getTimeZonedDate = (date: Date, timeZone?: string) => {
  if (!timeZone) return date
  return new Date(
    new Intl.DateTimeFormat('en-US', {
      dateStyle: 'short',
      timeStyle: 'medium',
      timeZone,
    }).format(date)
  )
}

export const getHoursOfOperationOnDay = (
  selectedDate: Date,
  HOURS_OF_OPERATIONS: ConvertedHoursOfOperation | null
) => {
  if (!HOURS_OF_OPERATIONS) {
    return [new Date(selectedDate), new Date(selectedDate)]
  }
  const day: number = selectedDate.getDay()
  const hoursOfOperation = HOURS_OF_OPERATIONS
  const {start, end} = hoursOfOperation[day as keyof typeof hoursOfOperation]

  const startHourDecimal = start - Math.floor(start)
  const newStart = setMinutes(setHours(new Date(selectedDate), start), startHourDecimal * 60)
  const endHourDecimal = end - Math.floor(end)
  const newEnd = setMinutes(setHours(new Date(selectedDate), end), endHourDecimal * 60)

  // if newEnd is before newStart, it means the hours of operation cross over midnight
  // so we need to add 24 hours to newEnd
  if (isBefore(newEnd, newStart) || isEqual(newEnd, newStart)) {
    return [newStart, addHours(newEnd, 24)]
  }

  return [newStart, newEnd]
}
export const getMinMaxHoursOfOperations = (
  HOURS_OF_OPERATIONS: ConvertedHoursOfOperation | null
): [DayHours, DayHours] => {
  if (!HOURS_OF_OPERATIONS) return [0, 24]

  const hoursOfOperation = HOURS_OF_OPERATIONS

  // add 24 hours to the end time if it is before the start time
  Object.keys(hoursOfOperation).forEach((key: any) => {
    const {start, end} = hoursOfOperation[key as keyof typeof hoursOfOperation]
    if (end < start) {
      hoursOfOperation[key as keyof typeof hoursOfOperation].end = end + 24
    }
  })

  const startHours = Object.values(hoursOfOperation).map((h) => h.start)
  const endHours = Object.values(hoursOfOperation).map((h) => h.end)

  const min = Math.min(...startHours) as DayHours
  const max = Math.max(...endHours) as DayHours

  // Keep past-midnight closes as >24 values (e.g. 00:45 next day = 24.75):
  // wrapping them back (max - 24) made the Week/EmployeesWeek views compute a
  // time range like 00:00→00:15 — a one-slot grid that clipped every shift out
  // of sight. The consumers' setHours/interval math handles >24 correctly.
  return [min, max]
}

export const toTitle = (text: string | undefined) => {
  if (!text) return ''
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// ---------------------------------------------------------------------------
// Position metadata registry — the data-driven replacement for hardcoded
// position semantics. LaborSchedulingWrapper populates it from the positions
// endpoint whenever the location changes; every color/sort/role lookup reads
// from it. Unknown keys degrade to today's defaults (crew-ish, blue, last).
// ---------------------------------------------------------------------------

const DEFAULT_POSITION_COLOR = '#6699cc'
let positionsMeta: Map<string, Position> = new Map()

export const setPositionsMeta = (positions: Position[] | undefined | null) => {
  positionsMeta = new Map((positions || []).map((p) => [p.position, p]))
}

export const getPositionMeta = (position: string | undefined | null): Position | undefined => {
  if (!position) return undefined
  return positionsMeta.get(position)
}

export const getPositionColor = (position: string | null | undefined): string => {
  return getPositionMeta(position)?.color || DEFAULT_POSITION_COLOR
}

// A shift with NO position at all — possible since templates, where the
// editor's position field is optional, and on the unassigned shifts an applied
// template creates. It must not fall through to getPositionColor's default:
// that default is the crew color (#6699cc, matching the backend resolver's),
// i.e. the color of 'emp'/Employee and every other crew position, so a blank
// shift rendered with it reads as an Employee shift. Neutral gray (the theme's
// secondary) says "no position yet" instead.
export const NO_POSITION_COLOR = '#6c757d'

// The card color for a scheduler event. EVERY path that builds or updates an
// event must use this — a shift's color must not depend on which path produced
// it. When the loader and the store disagreed here, the same position-less
// shift was one blue on creation and another after a reload.
export const getEventColor = (position: string | null | undefined): string =>
  position ? getPositionColor(position) : NO_POSITION_COLOR

export const getPositionDisplayName = (position: string | null | undefined): string => {
  // Position is optional since schedule templates: null degrades to ''.
  return getPositionMeta(position)?.name || toTitleCase(position || '')
}

// Replaces the hardcoded rgm > sl > rest comparators: sort_order from the
// position data (NULL last), then key alphabetically.
export const comparePositions = (a: string | undefined, b: string | undefined): number => {
  const soA = getPositionMeta(a)?.sort_order
  const soB = getPositionMeta(b)?.sort_order
  if (soA != null && soB != null && soA !== soB) return soA - soB
  if (soA != null && soB == null) return -1
  if (soA == null && soB != null) return 1
  return (a || '').localeCompare(b || '')
}

// Role classes whose hours are tracked separately (own chart series/rows),
// derived from the loaded positions. The color is the class's positions'
// resolved color (positions inherit their class color unless overridden), so
// chart series match the timeline/event colors.
export const getTrackedRoleClasses = (): {key: string; name: string; color: string}[] => {
  const seen = new Map<string, {name: string; color: string}>()
  positionsMeta.forEach((p) => {
    if (p.track_hours_separately && p.role_class && !seen.has(p.role_class)) {
      seen.set(p.role_class, {
        name: p.role_class_name || toTitleCase(p.role_class),
        color: p.color || DEFAULT_POSITION_COLOR,
      })
    }
  })
  return Array.from(seen, ([key, v]) => ({key, name: v.name, color: v.color}))
}

// Color for the residual "everyone else" chart series: the most common color
// among untracked schedulable positions, falling back to the default blue.
export const getResidualSeriesColor = (): string => {
  const counts = new Map<string, number>()
  positionsMeta.forEach((p) => {
    if (!p.track_hours_separately && p.is_schedulable !== false) {
      const c = p.color || DEFAULT_POSITION_COLOR
      counts.set(c, (counts.get(c) || 0) + 1)
    }
  })
  let best = DEFAULT_POSITION_COLOR
  let bestCount = 0
  counts.forEach((n, c) => {
    if (n > bestCount) {
      best = c
      bestCount = n
    }
  })
  return best
}

export const isPositionInRoleClass = (position: string | undefined, roleClass: string): boolean => {
  return getPositionMeta(position)?.role_class === roleClass
}

export const isTrackedSeparately = (position: string | undefined): boolean => {
  return getPositionMeta(position)?.track_hours_separately === true
}

export const convertBaseEmployeeToResource = (employee: BaseEmployeeModel): DefaultRecourse => {
  return {
    employee_id: employee.id,
    title: toTitleCase(employee.first_name) + ' ' + toTitleCase(employee.last_name),
    // `position` stays the canonical key (filtering); `position_name` is what
    // the resource header displays (subTextField).
    position: employee.position,
    position_name: getPositionDisplayName(employee.position),
    color: getPositionColor(employee.position),
  }
}

export const convertBaseEmployeesToResources = (
  employees: BaseEmployeeModel[],
  events: ProcessedEvent[] = []
): DefaultRecourse[] => {
  // Hide an employee only when they have NO shift in the loaded week AND are
  // inactive or hidden-in-schedule. On old schedules, employees who have since
  // left (or been hidden) must keep their row so their shifts stay labeled.
  const employeeIdsWithShifts = new Set(events.map((ev) => ev.employee_id))
  return employees
    .filter(
      (e) =>
        employeeIdsWithShifts.has(e.id) || (e.is_active !== false && e.hide_in_schedule !== true)
    )
    .map(convertBaseEmployeeToResource)
}

// Applied-template shifts have employee_id null in the API. Client-side, each
// unassigned shift carries a per-shift sentinel id ('unassigned-<shiftId>',
// set in convertLaborShiftToEvent) so it can own its OWN grid row — one row
// per unassigned shift, whose leading cell hosts the assign-employee picker.
// Payload builders translate any non-numeric employee_id back to null.
export const UNASSIGNED_EVENT_PREFIX = 'unassigned-'

export const isUnassignedEventRowId = (value: unknown): boolean =>
  typeof value === 'string' && value.startsWith(UNASSIGNED_EVENT_PREFIX)

// One pseudo-resource per unassigned event, pinned above the employee rows
// (they need attention), ordered by shift start.
export const withUnassignedShiftRows = (
  resources: DefaultRecourse[],
  events: ProcessedEvent[] = []
): DefaultRecourse[] => {
  const rows = events
    .filter((ev) => isUnassignedEventRowId(ev.employee_id))
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map((ev) => ({
      employee_id: ev.employee_id,
      title: 'Unassigned',
      position: ev.position || '',
      position_name: ev.position ? getPositionDisplayName(ev.position) : '',
      color: getEventColor(ev.position),
    }))
  return rows.length ? [...rows, ...resources] : resources
}

// The inverse, for the per-employee views (the week's resource tabs): an
// unassigned-shift row is a placeholder the week/timeline grids draw with an
// assign picker, not a person whose schedule can be browsed.
export const withoutUnassignedShiftRows = (resources: DefaultRecourse[]): DefaultRecourse[] =>
  resources.filter((r) => !isUnassignedEventRowId(r.employee_id))

// Timeline row order: unassigned-shift rows (visible day only) pinned on top,
// then employees with a shift that day, then position sort_order, then name.
// Template mode keeps the store's row-index order untouched. The has-event
// check keeps the historical getDate() day-of-month match (not isSameDay).
export const sortTimelineResources = (
  resources: DefaultRecourse[],
  events: ProcessedEvent[],
  selectedDate: Date,
  templateMode?: boolean
): DefaultRecourse[] => {
  if (templateMode) return resources
  // Unassigned rows cover the whole loaded week (a weekly template apply
  // creates one per day) — a single-day view only shows the visible day's.
  const unassignedRows = resources.filter((r) => {
    if (!isUnassignedEventRowId(r.employee_id)) return false
    const unassignedEvent = events.find((ev) => ev.employee_id === r.employee_id)
    return unassignedEvent ? isSameDay(unassignedEvent.start, selectedDate) : false
  })
  const idsWithEventOnDay = new Set(
    events.filter((x) => x.start.getDate() === selectedDate.getDate()).map((x) => x.employee_id)
  )
  const employeeRows = resources
    .filter((r) => !isUnassignedEventRowId(r.employee_id))
    .sort((a, b) => {
      const occurrenceA = +idsWithEventOnDay.has(a.employee_id)
      const occurrenceB = +idsWithEventOnDay.has(b.employee_id)
      if (occurrenceA === occurrenceB) {
        // Position order comes from the data-driven registry (sort_order).
        return (
          comparePositions(a.position, b.position) ||
          (a.first_name || '').localeCompare(b.first_name || '') ||
          (a.last_name || '').localeCompare(b.last_name || '')
        )
      }
      return occurrenceB - occurrenceA
    })
  return [...unassignedRows, ...employeeRows]
}

export const convertBaseEmployeeToAssigneeOption = (employee: BaseEmployeeModel) => {
  return {
    id: employee.id,
    text: toTitleCase(employee.first_name) + ' ' + toTitleCase(employee.last_name),
    value: employee.id,
  }
}

export const convertBaseEmployeesToAssigneeOptions = (employees?: BaseEmployeeModel[]) => {
  // New shifts can only be assigned to active, non-hidden employees — the
  // employee list now includes inactive ones (for labeling old schedules).
  // getEmployees resolves undefined on a failed request; an empty option
  // list keeps the rest of the page rendering.
  return (employees ?? [])
    .filter((e) => e.is_active !== false && e.hide_in_schedule !== true)
    .map(convertBaseEmployeeToAssigneeOption)
}

export const convertLaborShiftToEvent = (shift: LaborShiftModel): ProcessedEvent => {
  return {
    event_id: shift.id,
    title: getPositionDisplayName(shift.shift_role),
    position: shift.shift_role,
    start: new Date(shift.start_time),
    businessDay: shift.date_key,
    end: new Date(shift.end_time),
    allDay: false,
    // Unassigned shifts (applied templates) get a per-shift sentinel id so
    // each one owns a grid row with an assign-employee picker.
    employee_id: shift.employee_id ?? `${UNASSIGNED_EVENT_PREFIX}${shift.id}`,
    // Applied-template shifts can carry no role at all — those must not read as
    // crew shifts, so they get the neutral no-position color.
    color: getEventColor(shift.shift_role),
    // Both-or-neither, and null on most shifts. The event MUST carry these:
    // every write is a full replace, so a break that isn't on the event is a
    // break the next save deletes.
    break_start: shift.break_start_time ? new Date(shift.break_start_time) : null,
    break_end: shift.break_end_time ? new Date(shift.break_end_time) : null,
  }
}

export const convertLaborShifts = (shifts: LaborShiftModel[]): ProcessedEvent[] => {
  return shifts.map(convertLaborShiftToEvent)
}

/**
 * The break window after its shift moves to [newStart, newEnd].
 *
 * Translated by the same delta as the shift start, then dropped entirely if it
 * no longer fits — a drop can shorten a shift (the store clamps `end` to the
 * close of business), and a break hanging outside its shift is a 422 from the
 * API and a nonsense band on the board.
 */
export const translateBreak = (
  event: ProcessedEvent,
  newStart: Date,
  newEnd: Date
): {break_start: Date | null; break_end: Date | null} => {
  if (!event.break_start || !event.break_end) {
    return {break_start: null, break_end: null}
  }
  const delta = differenceInMinutes(newStart, event.start)
  const break_start = addMinutes(event.break_start, delta)
  const break_end = addMinutes(event.break_end, delta)
  if (break_start < newStart || break_end > newEnd) {
    return {break_start: null, break_end: null}
  }
  return {break_start, break_end}
}

/** Minutes of unpaid break on a shift; 0 when it has none. */
export const eventBreakMinutes = (event: ProcessedEvent): number => {
  if (!event.break_start || !event.break_end) {
    return 0
  }
  return Math.max(0, differenceInMinutes(event.break_end, event.break_start))
}

/**
 * Where the break sits inside its shift, as percentages of the shift's span.
 *
 * Percentages rather than pixels: the same two numbers place the band in every
 * time-proportional view without knowing that view's minute scale, and the
 * caller decides which axis carries time. Null when there is nothing to draw —
 * no break, a degenerate span, or a break that starts at the very end.
 */
export const breakBandGeometry = (event: ProcessedEvent): {offset: number; size: number} | null => {
  const minutes = eventBreakMinutes(event)
  const span = differenceInMinutes(event.end, event.start)
  if (!minutes || span <= 0) {
    return null
  }
  const offset = Math.min(
    100,
    Math.max(0, (differenceInMinutes(event.break_start, event.start) / span) * 100)
  )
  const size = Math.min(100 - offset, (minutes / span) * 100)
  return size > 0 ? {offset, size} : null
}

/**
 * Hours a shift actually puts on the floor: its span minus the unpaid break.
 *
 * The server aggregates the same way (labor_shift_actual.work_hours), so these
 * client-side totals must too — otherwise the board's own numbers disagree
 * with the weekly summary sitting next to them by exactly the break minutes.
 */
export const eventWorkHours = (event: ProcessedEvent): number =>
  (differenceInMinutes(event.end, event.start) - eventBreakMinutes(event)) / 60

export const extractEmployeeHours = (events: ProcessedEvent[]) => {
  const employeeHours: Map<number, number> = new Map()
  events.forEach((event) => {
    const hours = eventWorkHours(event)
    const currentHours = employeeHours.get(event.employee_id) || 0
    employeeHours.set(event.employee_id, currentHours + hours)
  })
  return employeeHours
}

export const extractHoursByDay = (events: ProcessedEvent[]) => {
  const hoursByDay: Map<string, number> = new Map() // for whole week key is 'week' for others it is string of date with 'yyyy-MM-dd' format
  events.forEach((event) => {
    const hours = eventWorkHours(event)
    const currentHours = hoursByDay.get('week') || 0
    hoursByDay.set('week', currentHours + hours)
    const date = format(event.start, 'yyyy-MM-dd')
    const currentHoursForDate = hoursByDay.get(date) || 0
    hoursByDay.set(date, currentHoursForDate + hours)
  })

  return hoursByDay
}

export const extractLaborCostByDay = (events: ProcessedEvent[], employees: BaseEmployeeModel[]) => {
  const laborCostByDay: Map<string, number> = new Map() // for whole week key is 'week' for others it is string of date with 'yyyy-MM-dd' format
  events.forEach((event) => {
    const employee = employees.find((e) => e.id === event.employee_id)
    if (!employee) return
    // Priced off worked hours: an unpaid break costs nothing.
    const hours = eventWorkHours(event)
    // A withheld rate (company hides pay) or an employee with none on file
    // costs 0 rather than turning the running total into NaN.
    const rate = employee.pay_amount ?? 0
    const currentCost = laborCostByDay.get('week') || 0
    laborCostByDay.set('week', currentCost + rate * hours)
    const date = format(event.start, 'yyyy-MM-dd')
    const currentCostForDate = laborCostByDay.get(date) || 0
    laborCostByDay.set(date, currentCostForDate + rate * hours)
  })

  return laborCostByDay
}
interface HoursOfOperation {
  operation_day_name: string
  start_time_for_eval: string
  end_time_for_eval: string
  end_day_for_eval?: string
}

export type WeekDays = 0 | 1 | 2 | 3 | 4 | 5 | 6

export const DAYS_OF_WEEK: {[key: string]: WeekDays} = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

export const convertHoursOfOperations = (
  hoursOfOperations: HoursOfOperation[]
): ConvertedHoursOfOperation => {
  const convertedHours: ConvertedHoursOfOperation = {}

  hoursOfOperations.forEach((h) => {
    const dayStr = h.operation_day_name.toLowerCase()
    const dayNum = DAYS_OF_WEEK[dayStr]

    const startTime = h.start_time_for_eval
    const endTime = h.end_time_for_eval

    const startHours = parseInt(startTime.split(':')[0])
    const startMinutes = parseInt(startTime.split(':')[1])
    const start = startHours + startMinutes / 60

    const endHours = parseInt(endTime.split(':')[0])
    const endMinutes = parseInt(endTime.split(':')[1])
    let end = endHours + endMinutes / 60
    // The API says explicitly when the close lands on the next day
    // (end_day_for_eval !== operation day) — e.g. 00:30 → 00:45 next day,
    // which the numeric end <= start heuristic alone cannot detect.
    const closesNextDay = !!h.end_day_for_eval && h.end_day_for_eval.toLowerCase() !== dayStr
    if (end <= start || closesNextDay) {
      end += 24
    }

    convertedHours[dayNum] = {start, end}
  })

  return convertedHours
}

/**
 * Is a slot correctly staffed — coverage inside the band it asks for?
 *
 * THE definition of a done slot, shared by both completion gauges and matching
 * what the timeline's Used row colours green. It reads the band, never the raw
 * allowable figure: on a slot whose staffing comes from the headcount measures
 * that figure is 0, so an equality test against it counts an empty slot as done
 * and a correctly staffed one as incomplete. Where an allowable figure IS set,
 * min and max both equal it and this is exactly the equality it replaced.
 */
export const isSlotCovered = (used: number, slot: SlotAllowableHours): boolean =>
  used >= slot.min_headcount && used <= slot.max_headcount

/** The slot's allowable figure for one position ('all' = every position's
 *  requirement stacked), or null when no row for the slot carries a figure —
 *  which is what sends the slot to its measure fallback. */
const allowableFigure = (
  hourData: HourAllowableHours | undefined,
  position: string
): number | null => {
  if (hourData === undefined) return null
  const rows = position === 'all' ? Object.values(hourData) : [hourData[position]]
  const figures = rows
    .filter((row) => row !== undefined)
    .map((row) => row.number_of_employees)
    .filter((count): count is number => count !== null && count !== undefined)
  // Not `figures.reduce(...) || null`: a genuine 0 is a figure ("nobody works
  // this slot") and must not fall through to the measure.
  if (figures.length === 0) return null
  return figures.reduce((acc, count) => acc + count, 0)
}

const isStrictSlot = (hourData: HourAllowableHours | undefined, position: string): boolean => {
  if (hourData === undefined) return false
  const rows = position === 'all' ? Object.values(hourData) : [hourData[position]]
  return rows.some((row) => row !== undefined && row.is_strict === true)
}

/**
 * A measure bound in whole people, or null when the company has no measure
 * configured for that bound (or the engine returned nothing).
 *
 * Rounded OUTWARD — floor for the floor, ceil for the ceiling — so that going
 * to whole people can only ever widen the band, never narrow it. A time-based
 * headcount measure routinely lands under 1 for a quiet slot (0.32 min / 0.40
 * max), and rounding both to nearest collapses that to a 0–0 band: the one
 * person actually on shift then reads as over the maximum and the slot turns
 * red. Rounding out gives 0–1, which is what the measure actually permits.
 */
/** The parameter key meaning "every role class not named". */
const DEFAULT_ROLE_CLASS_KEY = '*'

/**
 * The measure band that applies to one position in a slot.
 *
 * **The bounds are keyed by ROLE CLASS, not by position** (/labor/headcount-bounds
 * serves `labor_role_class.key`), because a staffing requirement is a statement
 * about a kind of person — "two managers at lunch" — and that is also the unit
 * the recommended-schedule generator pools staff in. The board's filter is a
 * POSITION, so the lookup goes through the position's own class.
 *
 * Its class's band, or the `*` one when nobody named that class. The **All**
 * filter does not come through here — it resolves each pool separately and adds
 * the results up, see `stackedBand`.
 */
const measureBandFor = (
  bounds: HourHeadcountBounds | undefined,
  position: string
): HeadcountSlotBounds | undefined => {
  if (!bounds) return undefined
  const roleClass = getPositionMeta(position)?.role_class
  return (roleClass ? bounds[roleClass] : undefined) || bounds[DEFAULT_ROLE_CLASS_KEY]
}

/**
 * The **All** filter's band: every pool resolved on its own, then added up.
 *
 * Each key is its own pool — `*` covers the classes nobody named, the rest
 * cover themselves — so the store's requirement is their total. That is what
 * this filter has always shown for allowable figures and what the
 * recommended-schedule generator staffs to (it emits one demand row per key).
 *
 * Resolved PER POOL rather than for the row as a whole, because the two sources
 * have different vocabularies and one slot can be covered by both: `emp` may
 * carry an allowable figure while `rgm` and `sl` have only their class band.
 * Summing the figures first and falling back only when NONE of them had one
 * dropped the banded pools from the total — a single surviving figure
 * suppressed the fallback for the whole row, so a slot asking for 5 crew plus
 * at most 1 manager plus at most 1 supervisor read as a flat 5-5 instead of
 * 5-7.
 *
 * The pool key is the ROLE CLASS, so the positions of one class contribute one
 * term between them and not one each — nine crew positions must not stack the
 * crew band nine times. A position with no class of its own falls in with `*`,
 * matching where `measureBandFor` would look its band up.
 *
 * Rounding happens per pool, BEFORE the sum, which keeps it outward: adding the
 * fractions first would floor two halves into a whole person neither pool asked
 * for, and it is also the shape the generator reads (one rounded demand row per
 * key).
 */
const stackedBand = (
  hourData: HourAllowableHours | undefined,
  bounds: HourHeadcountBounds | undefined
): {min: number; max: number} => {
  const figures = new Map<string, number>()
  Object.keys(hourData || {}).forEach((position) => {
    const count = hourData?.[position]?.number_of_employees
    // A genuine 0 is a figure ("nobody works this slot") and still claims its
    // pool; only a null or a missing row leaves the pool to its band.
    if (count === null || count === undefined) return
    const key = getPositionMeta(position)?.role_class || DEFAULT_ROLE_CLASS_KEY
    figures.set(key, (figures.get(key) || 0) + count)
  })

  const keys = Array.from(new Set(Array.from(figures.keys()).concat(Object.keys(bounds || {}))))
  return keys.reduce(
    (total, key) => {
      const figure = figures.has(key) ? (figures.get(key) as number) : null
      // The store-wide band covers the classes nobody named, so ANY figure on
      // the slot means the allowable table has already spoken for it — the
      // stand-down `fetch_demand` applies to `*` (a named class's band only
      // stands down for its own class's figure, which the key lookup below
      // already does). Without this the grid stacks a `*` band on top of
      // figures the recommended week counts once, and the two disagree about
      // what the slot asks for.
      if (key === DEFAULT_ROLE_CLASS_KEY && figure === null && figures.size > 0) {
        return total
      }
      const band = bounds ? bounds[key] : undefined
      return {
        min: total.min + (figure ?? measureBound(band?.min, 'floor') ?? 0),
        max: total.max + (figure ?? measureBound(band?.max, 'ceil') ?? 0),
      }
    },
    {min: 0, max: 0}
  )
}

const measureBound = (
  value: number | null | undefined,
  direction: 'floor' | 'ceil'
): number | null =>
  value === null || value === undefined
    ? null
    : direction === 'floor'
    ? Math.floor(value)
    : Math.ceil(value)

/**
 * One entry per hour in `hours`: the slot's allowable figure, plus the staffing
 * band the grid draws and judges against.
 *
 * The band is resolved per bound, per slot: **the location's allowable figure
 * when its row carries one, otherwise the company's configured min/max
 * headcount measure** (`min_headcount_measure` / `max_headcount_measure`,
 * served per slot by /labor/headcount-bounds). So an allowable row pins min and
 * max to the same number — which is the rule the grid has always drawn — while
 * a slot the allowable table says nothing about gets a real band from the
 * measures instead of the flat 0 it used to get. Measure bounds arrive
 * fractional and are rounded outward — see `measureBound`.
 *
 * On the **All** filter the same rule is applied per pool and the results added
 * up, so a slot where some positions carry a figure and others only have a band
 * shows both — see `stackedBand`.
 *
 * A slot with neither source reads as 0, as before.
 */
export const getAllowableValues = (
  allowableHoursDaily: DailyAllowableHours | null,
  selectedDate: Date,
  selectedPosition: string,
  hours: Date[],
  headcountBoundsDaily: DailyHeadcountBounds | null = null
): SlotAllowableHours[] => {
  const dateKey = format(selectedDate, 'yyyy-MM-dd')
  // Position keys are canonical (the server alias-resolves both this data and
  // the positions endpoint), so no client-side aliasing is needed here.
  const position = selectedPosition
  // Neither source loaded yet: return nothing, so callers that guard on
  // `length !== 0` stay quiet instead of judging the week against zeros.
  if (!allowableHoursDaily && !headcountBoundsDaily) return []

  const hoursData = allowableHoursDaily ? allowableHoursDaily[dateKey] : undefined
  const boundsData = headcountBoundsDaily ? headcountBoundsDaily[dateKey] : undefined

  return hours.map((hour) => {
    const timeKey = format(hour, 'HH:mm:ss')
    const hourData = hoursData ? hoursData[timeKey] : undefined
    const slotBounds = boundsData ? boundsData[timeKey] : undefined

    const figure = allowableFigure(hourData, position)
    // The allowable column stays the sum of the figures the slot actually
    // carries; only the BAND stacks the unfigured pools' bands in beside them.
    if (position === 'all') {
      const stacked = stackedBand(hourData, slotBounds)
      return {
        number_of_employees: figure ?? 0,
        is_strict: isStrictSlot(hourData, position),
        min_headcount: stacked.min,
        max_headcount: stacked.max,
      }
    }

    const bounds = measureBandFor(slotBounds, position)
    return {
      number_of_employees: figure ?? 0,
      is_strict: isStrictSlot(hourData, position),
      min_headcount: figure ?? measureBound(bounds?.min, 'floor') ?? 0,
      max_headcount: figure ?? measureBound(bounds?.max, 'ceil') ?? 0,
    }
  })
}

export const GetOldPageUrl = (url: string) => {
  return process.env.REACT_APP_OLD_FLASK_APP_URL + url
}

export const toTitleCase = (str: string) => {
  if (str === null || str === undefined) return str

  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export const convertAvailablePositionsToOptions = (positions: Position[]) => {
  // Editor dropdown: schedulable positions only (historical-only ones stay
  // resolvable for old shifts but can't be assigned to new ones).
  return positions
    .filter((p) => p.is_schedulable !== false)
    .sort((a, b) => comparePositions(a.position, b.position))
    .map((p, index) => ({
      value: p.position,
      text: p.name || toTitleCase(p.position),
      id: index,
    }))
}

export const getBusinessDay = (date: Date, hoursOfOperations: ConvertedHoursOfOperation | null) => {
  if (!hoursOfOperations) return date
  const dayOfWeek = date.getDay()
  const hours = hoursOfOperations[dayOfWeek]
  if (hours) {
    const start = hours.start
    const end = hours.end
    const dateHours = date.getHours() + date.getMinutes() / 60
    if (dateHours >= start && dateHours < end) {
      return date
    } else if (dateHours < start) {
      return subDays(date, 1)
    } else {
      return addDays(date, 1)
    }
  } else {
    return date
  }
}

export const getOverTimeHours = (events: ProcessedEvent[]) => {
  // If the employee has worked more than 40 hours in a week, the employee is eligible for overtime pay. Return {emp: ot_hours} object
  const employeeHours = extractHoursByEmployee(events)
  const employeeOvertimeHours: {[key: string]: number} = {}
  employeeHours.forEach((hours, employeeId) => {
    if (hours > 40) {
      employeeOvertimeHours[employeeId] = hours - 40
    }
  })
  return employeeOvertimeHours
}

export const extractHoursByEmployee = (events: ProcessedEvent[]) => {
  const hoursByEmployee: Map<string, number> = new Map()
  events.forEach((event) => {
    // Worked hours, not the clock span: overtime is owed on time worked, so an
    // unpaid break must never be what pushes someone over the 40-hour line.
    const hours = eventWorkHours(event)
    const currentHours = hoursByEmployee.get(event.employee_id) || 0
    hoursByEmployee.set(event.employee_id, currentHours + hours)
  })

  return hoursByEmployee
}

export const weekEquality = (week1: WeekProps | null, week2: WeekProps | null) => {
  if (!week1 || !week2) return false
  // compare weekdays
  if (week1.weekDays.length !== week2.weekDays.length) {
    return false
  }
  for (let i = 0; i < week1.weekDays.length; i++) {
    if (week1.weekDays[i] !== week2.weekDays[i]) return false
  }

  return (
    week1.weekStartOn === week2.weekStartOn &&
    week1.startHour === week2.startHour &&
    week1.endHour === week2.endHour &&
    week1.step === week2.step &&
    week1.navigation === week2.navigation &&
    week1.disableGoToDay === week2.disableGoToDay
  )
}
